import { useRef } from 'react';
import { Product } from 'entities/product';
import { CartItemFieldsFragment } from 'lib/shopify/generated/storefront';
import type { ShopifyCart } from 'context-providers/cart-provider';

type CartLineNode = CartItemFieldsFragment['lines']['nodes'][number];

const OPTIMISTIC_PREFIX = 'optimistic-';

export type OptimisticAdd = { sku: string; quantity: number; giftGID?: string };

export type OptimisticHandle = {
  mutationId: number;
  snapshot: {
    cart: Record<string, number>;
    fullCartInfo: ShopifyCart;
  };
  prevLineKeys: Array<{ id: string; sku: string }>;
};

type UseOptimisticCartArgs = {
  fullCartInfo: ShopifyCart;
  currentCartItems: Record<string, number>;
  setCurrentCartItems: (v: Record<string, number>) => void;
  setFullCartInfo: (v: ShopifyCart) => void;
  getProduct: (sku: string) => any;
  shopifyProducts: { [key: string]: Product['shopifyData'] };
};

export const useOptimisticCart = (args: UseOptimisticCartArgs) => {
  const {
    fullCartInfo,
    currentCartItems,
    setCurrentCartItems,
    setFullCartInfo,
    getProduct,
    shopifyProducts,
  } = args;

  // keeps things in order when multiple clicks fire
  const optimisticLineSeqRef = useRef(0);

  // tracks each change the user makes to the cart
  // this lets us drop stale responses if more changes happen
  const pendingMutationRef = useRef(0);

  const currency: CartItemFieldsFragment['cost']['subtotalAmount']['currencyCode'] =
    fullCartInfo?.cost?.subtotalAmount?.currencyCode || 'USD';

  const makeOptimisticLineId = (sku: string) =>
    `${OPTIMISTIC_PREFIX}${sku}-${++optimisticLineSeqRef.current}`;

  const makeOptimisticGiftLineId = (sku: string) =>
    `${OPTIMISTIC_PREFIX}gift-${sku}-${++optimisticLineSeqRef.current}`;

  const hasLineId = (l: {
    id?: string;
    quantity: number;
  }): l is { id: string; quantity: number } => !!l.id;

  const isOptimisticLineId = (id: any): boolean =>
    typeof id === 'string' && id.startsWith(OPTIMISTIC_PREFIX);

  // Sum quantity + estimated subtotal/total across a node array.
  const computeTotals = (nodes: CartLineNode[]) => {
    const totalQuantity = nodes.reduce(
      (sum, line) => sum + (line.quantity || 0),
      0,
    );
    const subtotal = nodes.reduce(
      (sum, line) =>
        sum + parseFloat(line.estimatedCost?.subtotalAmount?.amount || '0'),
      0,
    );
    const total = nodes.reduce(
      (sum, line) =>
        sum + parseFloat(line.estimatedCost?.totalAmount?.amount || '0'),
      0,
    );
    return { totalQuantity, subtotal, total };
  };

  // Build an optimistic fullCartInfo from a node array. Overrides lets callers
  // swap additional top-level fields (e.g. discountAllocations on add).
  const buildOptimisticFullCart = (
    nodes: CartLineNode[],
    overrides?: Partial<ShopifyCart>,
  ): ShopifyCart => {
    const { totalQuantity, subtotal, total } = computeTotals(nodes);
    const optimisticCurrency =
      fullCartInfo?.cost?.subtotalAmount?.currencyCode ||
      nodes[0]?.estimatedCost?.subtotalAmount?.currencyCode ||
      currency;
    const cost = {
      ...(fullCartInfo?.cost || {}),
      subtotalAmount: {
        amount: subtotal.toFixed(2),
        currencyCode: optimisticCurrency,
      },
    };
    // A pre-creation cart has no server total yet; estimate one so the footer
    // doesn't show 0 during the optimistic window. Existing totals are kept.
    if (!cost.totalAmount) {
      cost.totalAmount = {
        amount: total.toFixed(2),
        currencyCode: optimisticCurrency,
      };
    }
    return {
      ...fullCartInfo,
      totalQuantity,
      cost,
      lines: { ...(fullCartInfo?.lines || {}), nodes },
      ...(overrides || {}),
    };
  };

  // Reduce nodes to a { sku: quantity } map. Nodes without a sku are skipped.
  const nodesToCartMap = (nodes: CartLineNode[]): Record<string, number> =>
    nodes.reduce(
      (acc, line) => {
        const sku = line.merchandise?.sku;
        if (sku) acc[sku] = line.quantity;
        return acc;
      },
      {} as Record<string, number>,
    );

  // this keeps the cart items in consistent order across mutations, optimistic updates
  const stabilizeLinesOrder = (
    shopifyCart: ShopifyCart,
    prevLineKeys?: Array<{ id: string; sku: string }>,
  ): ShopifyCart => {
    if (!prevLineKeys?.length) return shopifyCart;
    const cartNodes = shopifyCart?.lines?.nodes;
    if (!cartNodes?.length) return shopifyCart;

    const respById = new Map<string, CartLineNode>(
      cartNodes.map((node) => [node.id, node]),
    );
    const respBySku = new Map<string, CartLineNode>(
      cartNodes
        .filter((node) => !!node.merchandise?.sku)
        .map((node) => [node.merchandise!.sku as string, node]),
    );

    const orderedNodes: CartLineNode[] = [];
    const placed = new Set<string>();
    for (const { id, sku } of prevLineKeys) {
      const match = respById.get(id) || (sku ? respBySku.get(sku) : undefined);
      if (match && !placed.has(match.id)) {
        orderedNodes.push(match);
        placed.add(match.id);
      }
    }
    for (const node of cartNodes) {
      if (!placed.has(node.id)) orderedNodes.push(node);
    }

    return {
      ...shopifyCart,
      lines: { ...shopifyCart.lines, nodes: orderedNodes },
    };
  };

  const repriceLine = (
    node: CartLineNode,
    newQuantity: number,
  ): CartLineNode => {
    const previousQuantity = node.quantity || 1;
    const previousSubtotal = parseFloat(
      node.estimatedCost?.subtotalAmount?.amount || '0',
    );
    const previousTotal = parseFloat(
      node.estimatedCost?.totalAmount?.amount || '0',
    );
    return {
      ...node,
      quantity: newQuantity,
      estimatedCost: {
        ...(node.estimatedCost || {}),
        subtotalAmount: {
          ...(node.estimatedCost?.subtotalAmount || {}),
          amount: ((previousSubtotal / previousQuantity) * newQuantity).toFixed(
            2,
          ),
        },
        totalAmount: {
          ...(node.estimatedCost?.totalAmount || {}),
          amount: ((previousTotal / previousQuantity) * newQuantity).toFixed(2),
        },
      },
    };
  };

  // when we hit updateCart, we send line items that contain product ID and the new quantity
  // using the products existing estimated subtotal/total/quantity, we can determine updated
  // subtotal/total. If there's a failure, fall back to awaiting Shopify.
  // lineUpdates is the payload being sent to updateCart - it's the future state of quantity
  const applyOptimisticCartUpdate = (
    lineUpdates: Array<{ id: string; quantity: number }>,
  ) => {
    // nodes is state of quantity before new updateCart call
    const nodes = fullCartInfo?.lines?.nodes;
    if (!nodes?.length) return null;

    const existingLineIds = new Set(nodes.map((n) => n.id));
    const allLinesExist = lineUpdates.every((l) => existingLineIds.has(l.id));
    if (!allLinesExist) return null;
    const updatesByLineId = new Map(lineUpdates.map((line) => [line.id, line]));

    // lineUpdates contains the new quantity
    // we can use this to determine expected subtotals and totals when the quantity updates
    const newNodes = nodes
      .map((node) => {
        const update = updatesByLineId.get(node.id);
        if (!update) return node;
        if (update.quantity === 0) return null;
        return repriceLine(node, update.quantity);
      })
      .filter((n): n is CartLineNode => n !== null);

    const optimisticFullCart = buildOptimisticFullCart(newNodes);
    // returns object like {"syn-wk": 2, "dm-wk": 1}
    const optimisticCart = nodesToCartMap(newNodes);

    return { cart: optimisticCart, fullShopifyCart: optimisticFullCart };
  };

  // Apply an optimistic update and return a handle used to reconcile or roll back
  // once the real Shopify response lands. Returns null when optimistic isn't safe
  // (no cartID, creating new lines, or snapshot mismatch) — caller keeps legacy flow.
  const tryOptimisticUpdate = (
    cartID: string | undefined,
    lines: Array<{ id?: string; quantity: number }>,
  ): OptimisticHandle | null => {
    if (!cartID) return null;
    if (!lines.every(hasLineId)) return null;

    const optimistic = applyOptimisticCartUpdate(lines);
    if (!optimistic) return null;

    const snapshot = { cart: currentCartItems, fullCartInfo };
    const prevLineKeys: Array<{ id: string; sku: string }> = (
      fullCartInfo?.lines?.nodes || []
    ).map((n) => ({ id: n.id, sku: n.merchandise?.sku ?? '' }));
    const mutationId = ++pendingMutationRef.current;
    setCurrentCartItems(optimistic.cart);
    setFullCartInfo(optimistic.fullShopifyCart);
    return { mutationId, snapshot, prevLineKeys };
  };

  // uses cached product data to optimistically add new product sku to the cart
  const buildOptimisticNewLineNode = (sku: string, quantity: number) => {
    const product = getProduct(sku);
    const shopifyData = product?.shopifyData;
    if (!product || !shopifyData) return null;

    const unitPrice =
      parseFloat(shopifyData.price?.amount || '') ||
      product.domesticAttributes?.unitPrice ||
      0;

    if (!unitPrice || Number.isNaN(unitPrice)) return null;
    const subtotalAmount = (unitPrice * quantity).toFixed(2);
    // Before the first cart exists there's no cart currency — use the
    // product's own market-localized price currency, not the USD fallback.
    const lineCurrency =
      fullCartInfo?.cost?.subtotalAmount?.currencyCode ||
      shopifyData.price?.currencyCode ||
      currency;
    const sellingPlanId =
      shopifyData.sellingPlanAllocations?.edges?.[0]?.node?.sellingPlan?.id;

    return {
      id: makeOptimisticLineId(sku),
      quantity,
      attributes: [],
      merchandise: {
        id: shopifyData.id,
        sku: shopifyData.sku || sku,
        title: shopifyData.title,
        compareAtPrice: null,
        image: shopifyData.featuredImage,
        product: { title: shopifyData.title },
      },
      sellingPlan: sellingPlanId ? { id: sellingPlanId } : undefined,
      estimatedCost: {
        subtotalAmount: { amount: subtotalAmount, currencyCode: lineCurrency },
        totalAmount: { amount: subtotalAmount, currencyCode: lineCurrency },
      },
      discountAllocations: [],
    };
  };

  // adds a line for any gifts that are associated with newly added skus.
  const buildOptimisticGiftLineNode = (
    giftShopifyData: Product['shopifyData'],
    quantity: number,
  ) => {
    if (!giftShopifyData?.sku || !giftShopifyData?.id) return null;
    const giftCurrency =
      fullCartInfo?.cost?.subtotalAmount?.currencyCode ||
      giftShopifyData.price?.currencyCode ||
      currency;
    return {
      id: makeOptimisticGiftLineId(giftShopifyData.sku),
      quantity,
      attributes: [{ key: '_isGiftWithPurchase', value: 'true' }],
      merchandise: {
        id: giftShopifyData.id,
        sku: giftShopifyData.sku,
        title: giftShopifyData.title,
        compareAtPrice: null,
        image: giftShopifyData.featuredImage,
        product: { title: giftShopifyData.title },
      },
      estimatedCost: {
        subtotalAmount: { amount: '0.00', currencyCode: giftCurrency },
        totalAmount: { amount: '0.00', currencyCode: giftCurrency },
      },
      discountAllocations: [],
    };
  };

  // Show the whole batch together, or wait for Shopify if any line is incomplete.
  const tryOptimisticAddMany = (
    items: OptimisticAdd[],
    lines: Array<{ id?: string; quantity: number }> = [],
  ): OptimisticHandle | null => {
    const existingUpdates = lines.filter(hasLineId);
    if (!items.length && !existingUpdates.length) return null;

    const updated = applyOptimisticCartUpdate(existingUpdates);
    if (existingUpdates.length && !updated) return null;
    const prevNodes =
      updated?.fullShopifyCart.lines.nodes ?? fullCartInfo?.lines?.nodes ?? [];

    const newLines: CartLineNode[] = [];
    for (const { sku, quantity, giftGID } of items) {
      const newLine = buildOptimisticNewLineNode(sku, quantity);
      if (!newLine) return null;
      newLines.push(newLine as CartLineNode);

      // add a gift line item if there's a gift
      if (giftGID) {
        if (prevNodes.some((line) => line.merchandise.id === giftGID)) continue;
        const sharedGift = newLines.find(
          (line) => line.merchandise.id === giftGID,
        );
        if (sharedGift) {
          sharedGift.quantity = quantity;
          continue;
        }
        const giftShopifyData = Object.values(shopifyProducts || {}).find(
          (p) => p?.id === giftGID,
        );
        if (!giftShopifyData) return null;
        const giftLine = buildOptimisticGiftLineNode(giftShopifyData, quantity);
        if (!giftLine) return null;
        newLines.push(giftLine as CartLineNode);
      }
    }

    // New items go first; existing products and gifts keep their positions.
    const newNodes: CartLineNode[] = [...newLines, ...prevNodes];

    // cart-items.tsx aligns discountAllocations by index against lines.nodes —
    // appending a line without a matching entry would mismatch the counts and
    // cause the filter-fallback branch to hide discounts on other rows during
    // the optimistic window. Append a zero-amount placeholder per new line
    // (one for the main product, plus one for the gift when present).
    const prevDiscountAllocations = fullCartInfo?.discountAllocations || [];
    const placeholderAllocation = {
      discountedAmount: { amount: '0', currencyCode: currency },
    } as unknown as CartItemFieldsFragment['discountAllocations'][number];
    const newDiscountAllocations = [
      ...newLines.map(() => placeholderAllocation),
      ...prevDiscountAllocations,
    ];

    const optimisticFullCart = buildOptimisticFullCart(newNodes, {
      discountAllocations: newDiscountAllocations,
    });

    const optimisticCart = nodesToCartMap(newNodes);

    // these are generated to roll back to in case of error
    const snapshot = { cart: currentCartItems, fullCartInfo };
    const prevLineKeys: Array<{ id: string; sku: string }> = newNodes.map(
      (n) => ({ id: n.id, sku: n.merchandise?.sku ?? '' }),
    );
    const mutationId = ++pendingMutationRef.current;
    setCurrentCartItems(optimisticCart);
    setFullCartInfo(optimisticFullCart);
    return { mutationId, snapshot, prevLineKeys };
  };

  // called when the shopify response errors - we rollback to the snapshot taken
  // before optimistic state was inferred
  const rollbackOptimisticIfLatest = (handle: OptimisticHandle | null) => {
    if (!handle) return;
    if (handle.mutationId !== pendingMutationRef.current) return;
    setCurrentCartItems(handle.snapshot.cart);
    setFullCartInfo(handle.snapshot.fullCartInfo);
  };

  // swap most recent fake (optimistic) data for real Shopify data once it's available, preserving row order
  const reconcile = (
    handle: OptimisticHandle | null,
    fullShopifyCart: ShopifyCart,
  ) => {
    if (handle && handle.mutationId !== pendingMutationRef.current) return;
    const stabilizedFull = stabilizeLinesOrder(
      fullShopifyCart,
      handle?.prevLineKeys ??
        (fullCartInfo?.lines?.nodes || []).map((line) => ({
          id: line.id,
          sku: line.merchandise?.sku ?? '',
        })),
    );
    // Derive cart (sku→qty) from the stabilized nodes so its Object.keys order
    // matches the lines array — cart-items.tsx iterates cart keys for row order.
    setCurrentCartItems(nodesToCartMap(stabilizedFull?.lines?.nodes || []));
    setFullCartInfo(stabilizedFull);
  };

  const latestMutationId = () => pendingMutationRef.current;

  return {
    tryOptimisticUpdate,
    tryOptimisticAddMany,
    rollbackOptimisticIfLatest,
    reconcile,
    isOptimisticLineId,
    latestMutationId,
  };
};
